import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "../assets/css/dash-style.css";
import { useDispatch, useSelector } from "react-redux";
import { signOutUser } from "../../store/slice/user";
import { toast } from "react-toastify";
import { emptyCart } from "../../store/slice/cart";
import isValidURL from "../../ValidateUrl";
import { handleGetPendingOrdersForChef } from "../../services/order";
import { handleGetAllDishes, handleToggleAllDishesStatus } from "../../services/shef";

export const Header = () => {
  const [isSubMenuVisible, setSubMenuVisible] = useState(false);
  const [isMobMenuVisible, setMobMenuVisible] = useState(false);
  const [isProfileMenuVisible, setProfileMenuVisible] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Create refs for dropdowns to handle outside clicks
  const subMenuRef = useRef(null);
  const mobMenuRef = useRef(null);
  const profileMenuRef = useRef(null);

  // User from redux
  const { userInfo, authToken } = useSelector((state) => state.user);

  // Toggle functions
  const toggleBox = () => {
    setSubMenuVisible(!isSubMenuVisible);
  };

  const toggleMobBox = () => {
    setMobMenuVisible(!isMobMenuVisible);
  };

  // Chef Pending Order notification
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  useEffect(() => {
    let intervalId;

    const fetchPendingOrderCount = async () => {
      try {
        const pendingOrderResponse = await handleGetPendingOrdersForChef(
          userInfo.id,
          authToken
        );
        setPendingOrderCount(pendingOrderResponse);
        if (pendingOrderResponse > 0) {
          toast.dismiss();
          toast(`You have ${pendingOrderResponse} Pending Orders`);
        }
        console.log("response of pending order ", pendingOrderResponse);
      } catch (error) {
        console.error("Error while fetching Pending orders \n", error);
      }
    };

    if (userInfo.is_chef === 1 || userInfo.is_admin === 1) {
      fetchPendingOrderCount(); // Call it immediately on component mount if needed.

      intervalId = setInterval(() => {
        fetchPendingOrderCount();
      }, 300000); // 5 minutes in milliseconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId); // Clear interval on unmount or dependency change
    };
  }, [authToken, userInfo.id, userInfo.is_chef, userInfo.is_admin]);

  // Kitchen On/Off toggle — shef dashboard only
  const [kitchenOn, setKitchenOn] = useState(false);
  const [kitchenBusy, setKitchenBusy] = useState(false);

  const refreshKitchenState = async () => {
    try {
      const dishes = await handleGetAllDishes(authToken);
      setKitchenOn(Array.isArray(dishes) && dishes.some((d) => !!d.is_live));
    } catch (e) {
      // keep previous state on failure
    }
  };

  useEffect(() => {
    if (userInfo?.is_chef === 1 && authToken) {
      refreshKitchenState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.is_chef, authToken]);

  // Keep kitchen state in sync when dishes are toggled elsewhere
  // (e.g. the Kitchen button on the My Menu page)
  useEffect(() => {
    const onKitchenChanged = () => refreshKitchenState();
    window.addEventListener("kitchen:changed", onKitchenChanged);
    return () => window.removeEventListener("kitchen:changed", onKitchenChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const handleKitchenToggle = async () => {
    if (
      kitchenOn &&
      !window.confirm(
        "Turn your kitchen OFF? All dishes will be hidden except those with accepted orders pending delivery."
      )
    ) {
      return;
    }
    try {
      setKitchenBusy(true);
      const res = await handleToggleAllDishesStatus(authToken, !kitchenOn);
      if (res?.message) toast.success(res.message);
      // Notify other components (My Menu page listens and refreshes dishes)
      window.dispatchEvent(new Event("kitchen:changed"));
    } catch (err) {
      toast.error(err.message || "Failed to update kitchen status");
    } finally {
      setKitchenBusy(false);
    }
  };

  // Logout
  const handleSignOut = () => {
    dispatch(signOutUser());
    dispatch(emptyCart());
    toast.success("Logout Successfully ");
    navigate("/login");
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subMenuRef.current && !subMenuRef.current.contains(event.target)) {
        setSubMenuVisible(false);
      }
      if (
        isMobMenuVisible &&
        mobMenuRef.current &&
        !mobMenuRef.current.contains(event.target) &&
        !event.target.closest("#mobileMenuToggle")
      ) {
        setMobMenuVisible(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobMenuVisible]);

  return (
    <>
      <Helmet>
        <title>Shef Dashboard | Shefs Cloud</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&display=swap"
          rel="stylesheet"
        ></link>
      </Helmet>
      <nav className="bg-white border-b fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Mobile Menu Toggle - visible only on mobile */}
            <button
              onClick={toggleMobBox}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              id="mobileMenuToggle"
            >
              {isMobMenuVisible ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M11.9997 10.5865L16.9495 5.63672L18.3637 7.05093L13.4139 12.0007L18.3637 16.9504L16.9495 18.3646L11.9997 13.4149L7.04996 18.3646L5.63574 16.9504L10.5855 12.0007L5.63574 7.05093L7.04996 5.63672L11.9997 10.5865Z"></path>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M3 4H21V6H3V4ZM3 11H15V13H3V11ZM3 18H21V20H3V18Z"></path>
                </svg>
              )}
            </button>

            {/* Logo - visible on all screens */}
            <NavLink to="/" className="flex items-center space-x-3">
              <img
                src="/media/frontend/img/logo/new/shefscloud_logo_2.png"
                className="h-10 w-auto mb-2"
                alt="ShefsCloud Logo"
              />
            </NavLink>

            {/* Desktop Navigation - hidden on mobile, shown on desktop */}
            <div className="hidden lg:flex items-center space-x-4">
              <ul className="flex items-center my-auto ml-8">
                <li className="lg:inline-block shefD_nav mr-5">
                  <NavLink
                    to="/shef/dashboard"
                    className="text-lg font-semibold !text-secondary hover:!text-primary"
                  >
                    Dashboard
                  </NavLink>
                </li>
                <li className="lg:inline-block shefD_nav mr-5">
                  <NavLink
                    to="/shef/my-menu"
                    className="text-lg font-semibold !text-secondary hover:!text-primary"
                  >
                    My Menu
                  </NavLink>
                </li>
                <li className="lg:inline-block shefD_nav mr-5">
                  <NavLink
                    to="/shef/order"
                    className="text-lg font-semibold !text-secondary hover:!text-primary"
                  >
                    Orders
                  </NavLink>
                </li>
                <li className="lg:inline-block shefD_nav mr-5">
                  <NavLink
                    to="/shef/kitchen-offline"
                    className="text-lg font-semibold !text-secondary hover:!text-primary"
                  >
                    Kitchen Offline
                  </NavLink>
                </li>

                {/* Reports dropdown with ref */}
                <li className="relative" ref={subMenuRef}>
                  <button
                    onClick={toggleBox}
                    className="text-lg font-semibold text-secondary hover:text-primary flex items-center gap-x-3"
                  >
                    <span>Reports</span>
                    {isSubMenuVisible ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="rgba(0,0,0,1)"
                      >
                        <path d="M11.9997 10.5865L16.9495 5.63672L18.3637 7.05093L13.4139 12.0007L18.3637 16.9504L16.9495 18.3646L11.9997 13.4149L7.04996 18.3646L5.63574 16.9504L10.5855 12.0007L5.63574 7.05093L7.04996 5.63672L11.9997 10.5865Z"></path>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="rgba(0,0,0,1)"
                      >
                        <path d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"></path>
                      </svg>
                    )}
                  </button>
                  {/* Open Menu Box */}
                  {isSubMenuVisible && (
                    <ul className="absolute left-0 top-9 bg-white w-[200px] p-3 rounded-md border border-borderClr z-50 shadow-lg">
                      <li className="block border-b border-borderClr mb-2 pb-2">
                        <NavLink
                          to="/shef/sales-statment"
                          className="text-lg font-semibold !text-secondary hover:!text-primary"
                        >
                          Sales Statement
                        </NavLink>
                      </li>
                      <li className="block border-b border-borderClr mb-2 pb-2">
                        <NavLink
                          to="/shef/coupon"
                          className="text-lg font-semibold !text-secondary hover:!text-primary"
                        >
                          Coupon
                        </NavLink>
                      </li>
                      <li className="block">
                        <NavLink
                          to="/shef/order-review"
                          className="text-lg font-semibold !text-secondary hover:!text-primary"
                        >
                          Order Reviews
                        </NavLink>
                      </li>
                    </ul>
                  )}
                </li>
              </ul>
            </div>

            {/* Profile Section - visible on all screens */}
            <div className="flex items-center gap-3 relative">
              {/* Kitchen On/Off toggle — shef dashboard */}
              {userInfo?.is_chef === 1 && (
                <button
                  onClick={handleKitchenToggle}
                  disabled={kitchenBusy}
                  title={
                    kitchenOn
                      ? "Turn kitchen OFF — hide all dishes"
                      : "Turn kitchen ON — reactivate all dishes"
                  }
                  className={`hidden md:block px-3 py-2 rounded-lg font-medium text-sm transition-colors border disabled:opacity-50 whitespace-nowrap text-black ${
                    kitchenOn
                      ? "border-red-600 hover:bg-red-50"
                      : "border-green-600 hover:bg-green-50"
                  }`}
                >
                  {kitchenBusy ? (
                    "Updating..."
                  ) : (
                    <>
                      Kitchen{" "}
                      <span className={kitchenOn ? "text-red-600" : "text-green-600"}>
                        {kitchenOn ? "Off" : "On"}
                      </span>
                    </>
                  )}
                </button>
              )}

              {/* Notification icon - show on all screens */}
              {pendingOrderCount > 0 && (
                <NavLink to="/shef/order" className="relative">
                  <div className="notifyBx">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="rgba(255,255,255,1)"
                    >
                      <path d="M5 18H19V11.0314C19 7.14806 15.866 4 12 4C8.13401 4 5 7.14806 5 11.0314V18ZM12 2C16.9706 2 21 6.04348 21 11.0314V20H3V11.0314C3 6.04348 7.02944 2 12 2ZM9.5 21H14.5C14.5 22.3807 13.3807 23.5 12 23.5C10.6193 23.5 9.5 22.3807 9.5 21Z"></path>
                    </svg>
                    <span className="notifyDot"></span>
                  </div>
                </NavLink>
              )}
              
              {/* Profile dropdown with ref */}
              <div className="relative" ref={profileMenuRef}>
                <button 
                  onClick={() => setProfileMenuVisible(!isProfileMenuVisible)}
                  className="flex items-center gap-2"
                >
                  <img
                    src={userInfo.profile_pic && isValidURL(userInfo.profile_pic) 
                         ? userInfo.profile_pic 
                         : "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"}
                    className="w-10 h-10 rounded-full border object-cover"
                    alt="Profile"
                  />
                  <span className="font-semibold text-lg hidden md:block">
                    Hi, {userInfo.first_name}
                  </span>
                  <svg
                    className="w-2.5 h-2.5 ms-3 ml-1"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 10 6"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 4 4 4-4"
                    />
                  </svg>
                </button>
                
                {/* UPDATED DROPDOWN CONTENT MATCHING NORMAL HEADER */}
                {isProfileMenuVisible && (
                  <div className="absolute right-0 mt-2 w-56 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {(userInfo.is_chef === 1 || userInfo.is_admin === 1) && (
                        <NavLink
                          to="/"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setProfileMenuVisible(false)}
                        >
                          Home
                        </NavLink>
                      )}
                      <NavLink
                        to="/order"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setProfileMenuVisible(false)}
                      >
                        Orders
                      </NavLink>
                      {(userInfo.is_chef === 1 || userInfo.is_admin === 1) && (
                        <NavLink
                          to="/shef/order"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setProfileMenuVisible(false)}
                        >
                          Shef Orders
                        </NavLink>
                      )}
                      <NavLink
                        to={userInfo.is_chef === 1 || userInfo.is_admin === 1 ? "/shef/profile" : "/profile"}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setProfileMenuVisible(false)}
                      >
                        Account
                      </NavLink>
                      <button
                        onClick={handleSignOut}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
                {/* END UPDATED DROPDOWN */}
              </div>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobMenuVisible && (
            <div 
              ref={mobMenuRef} 
              className="lg:hidden mt-4 bg-white rounded-lg shadow-lg p-4 z-50"
            >
              <ul className="space-y-3">
                <li>
                  <NavLink
                    to="/shef/dashboard"
                    className="block py-2 px-4 rounded hover:bg-gray-100 text-lg font-semibold text-secondary"
                    onClick={() => setMobMenuVisible(false)}
                  >
                    Dashboard
                  </NavLink>
                </li>

                {/* Kitchen On/Off toggle */}
                {userInfo?.is_chef === 1 && (
                  <li>
                    <button
                      onClick={() => {
                        setMobMenuVisible(false);
                        handleKitchenToggle();
                      }}
                      disabled={kitchenBusy}
                      className="block w-full text-left py-2 px-4 rounded hover:bg-gray-100 text-lg font-semibold disabled:opacity-50 text-black"
                    >
                      Kitchen{" "}
                      <span className={kitchenOn ? "text-red-600" : "text-green-600"}>
                        {kitchenOn ? "Off" : "On"}
                      </span>
                    </button>
                  </li>
                )}
                <li>
                  <NavLink
                    to="/shef/my-menu"
                    className="block py-2 px-4 rounded hover:bg-gray-100 text-lg font-semibold text-secondary"
                    onClick={() => setMobMenuVisible(false)}
                  >
                    My Menu
                  </NavLink>
                </li>
                <li>
                <li>
                  <NavLink
                    to="/shef/kitchen-offline"
                    className="block py-2 px-4 rounded hover:bg-gray-100 text-lg font-semibold text-secondary"
                    onClick={() => setMobMenuVisible(false)}
                  >
                    Kitchen Offline
                  </NavLink>
                </li>
  <NavLink
                    to="/shef/order"
                    className="block py-2 px-4 rounded hover:bg-gray-100 text-lg font-semibold text-secondary"
                    onClick={() => setMobMenuVisible(false)}
                  >
                    Orders
                  </NavLink>
                </li>
                
                {/* Reports dropdown in mobile */}
                <li>
                  <div className="py-2 px-4">
                    <span className="text-lg font-semibold text-secondary">Reports</span>
                    <ul className="mt-2 pl-4 space-y-2">
                      <li>
                        <NavLink
                          to="/shef/sales-statment"
                          className="block py-1 text-secondary hover:text-primary"
                          onClick={() => setMobMenuVisible(false)}
                        >
                          Sales Statement
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/shef/coupon"
                          className="block py-1 text-secondary hover:text-primary"
                          onClick={() => setMobMenuVisible(false)}
                        >
                          Coupon
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/shef/order-review"
                          className="block py-1 text-secondary hover:text-primary"
                          onClick={() => setMobMenuVisible(false)}
                        >
                          Order Reviews
                        </NavLink>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
          )}
        </div>
      </nav>
      <div className="h-16"></div>
    </>
  );
};

export default Header;